import { FilterSchema } from "@generation/core";
import * as React from "react";
import { useFormContext } from "react-hook-form";
import { RenderFields } from "../Form/RenderFields";
import { useFilterFormValuesContext } from "./Context/FilterFormValuesContext";

import { HintPopupContextProvider } from "../Context/HintPopupContextProvider";
import "../Form/Form.scss";
import { RenderFormButtons } from "../Form/FormButtons";
import { withForm } from "../Hoc/withForm";
import { IFormProps } from "../Index";
import { isResetButton, isSubmitButton } from "../Utils/buttons";
import { convertButtonsToList } from "../Utils/convertButtonsToList";

import { FieldRenderContextProvider } from "../Context/FieldRenderContextProvider";
import { FormPropsContextProvider } from "../Context/FormPropsContextProvider";
import { arrayFieldRenderer } from "../render/arrayFieldRenderer";
import { defaultFieldRenderer } from "../render/defaultFieldRenderer";
import { objectFieldRenderer } from "../render/objectFieldRenderer";
import { readItemFieldRenderer } from "../render/readItemFieldRenderer";
import { dynamicFieldRenderer } from "../render/dynamicFieldRenderer";

export interface IFilterFormProps extends IFormProps<FilterSchema> {
  cols: number;
}

export const FilterForm = withForm(
  React.memo((props: IFilterFormProps) => {
    const form = useFormContext();

    const filterFormValues = useFilterFormValuesContext();

    const buttons = convertButtonsToList(props);

    return (
      <FormPropsContextProvider value={props}>
        <FieldRenderContextProvider
          value={{
            array: arrayFieldRenderer,
            default: defaultFieldRenderer,
            object: objectFieldRenderer,
            readItem: readItemFieldRenderer,
            dynamic: dynamicFieldRenderer,
          }}
        >
          <HintPopupContextProvider>
            <form
              onSubmit={form.handleSubmit((values: any, event: React.FormEvent) => {
                filterFormValues.setValues(values);

                const button = buttons.find(isSubmitButton);

                if (button && button.handle) {
                  button.handle(values, event, {
                    setError: (property: string, message: string, type: string) =>
                      form.setError(property, { message: message, type: type }),
                    setValues: (items) => {
                      items.forEach((value) => {
                        form.setValue(value.key, value.value, { shouldValidate: true });
                      });
                    },
                    reset: form.reset
                  });
                }
              })}
              autoComplete="off"
              onChange={props.onChange}
              onReset={(event: React.FormEvent) => {
                //Alleen formulier waardes worden gereset als deze expliciet op "" worden gezet.
                //Dit komt door de inputfield in shared/common
                const defaultValues = props.formOptions?.defaultValues ?? {};
                Object.entries(props.schema).forEach((sectie) => {
                  if (!(sectie[0] in defaultValues) || defaultValues[sectie[0]] === undefined) {
                    defaultValues[sectie[0]] = "";
                  }
                });

                form.reset(defaultValues, {
                  keepErrors: false,
                  keepDirty: false,
                  keepIsSubmitted: false,
                  keepTouched: false,
                  keepIsValid: false,
                  keepSubmitCount: false
                });
                filterFormValues.setValues(defaultValues);

                const button = buttons.find(isResetButton);
                button?.handle?.(event);
              }}
            >
              <div className={props.hide !== true ? "show" : "hide"}>
                <div className="form-row">
                  {Object.entries(props.schema).map((sectie, index) => (
                    <React.Fragment key={`sectie${index}`}>
                      {sectie[1].ui?.filterOverview?.lineBreak === true && <div className="break" />}
                      <div className={`col-md-${props.cols} cak-mb-${props.cols}`} key={`${sectie[0]}`}>
                        <RenderFields field={{ ...sectie[1], icon: "search" }} name={sectie[0]} />
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                <RenderFormButtons buttons={buttons} implementClickHandlers={false} />
              </div>
            </form>
          </HintPopupContextProvider>
        </FieldRenderContextProvider>
      </FormPropsContextProvider>
    );
  })
);
